/**
 * Session-scoped run history: slim per-run records (NOT full
 * MetricsResult) plus delta computation between runs of the same
 * config. In-memory only by design — refresh clears it.
 */

export interface RunRecord {
  /** Unique within the session, assigned by the caller on append. */
  id: number;
  /** Date.now() at run completion. */
  completedAt: number;
  /** Seed the run STARTED with (snapshot, not live input state). */
  seed: string;
  nSpins: number;
  /** metrics.config_hash — authoritative, present for uploaded IRs too. */
  configHash: string;
  engineLabel: string;
  elapsedMs: number;
  rtpPct: number;
  ciHalfPct: number;
  hitPct: number;
  stdev: number;
  maxWin: number;
}

/** ~one session's worth of tune iterations without overwhelming the panel. */
export const HISTORY_CAP = 20;

/** Prepend a record (history is newest-first); trim to HISTORY_CAP.
 *  Returns a new array. */
export function prependRun(
  history: RunRecord[],
  record: RunRecord,
): RunRecord[] {
  return [record, ...history].slice(0, HISTORY_CAP);
}

export interface RunDeltas {
  rtpPct: number;
  hitPct: number;
  stdev: number;
  maxWin: number;
}

/**
 * For each record (newest-first array), deltas vs the next-oldest record
 * with the SAME configHash; null when no same-hash predecessor exists.
 */
export function withDeltas(
  history: RunRecord[],
): Array<{ record: RunRecord; deltas: RunDeltas | null }> {
  return history.map((record, i) => {
    const prev = history
      .slice(i + 1)
      .find((r) => r.configHash === record.configHash);
    return {
      record,
      deltas:
        prev == null
          ? null
          : {
              rtpPct: record.rtpPct - prev.rtpPct,
              hitPct: record.hitPct - prev.hitPct,
              stdev: record.stdev - prev.stdev,
              maxWin: record.maxWin - prev.maxWin,
            },
    };
  });
}
